import { useRef } from "react";
import BrandingPreviewCanvas from "../BrandingPreviewCanvas.jsx";

export default function BrandingSettingsModal({
  pendingCompanyName,
  setPendingCompanyName,
  logoUrl,
  onLogoImport,
  onRemoveLogo,
  pendingLogoAlignment,
  setPendingLogoAlignment,
  pendingLogoMargin,
  setPendingLogoMargin,
  pendingLogoScale,
  setPendingLogoScale,
  pendingTextAlignment,
  setPendingTextAlignment,
  pendingTextMargin,
  setPendingTextMargin,
  pendingTextScale,
  setPendingTextScale,
  pendingSymbolColor,
  setPendingSymbolColor,
  showBrandingPreview,
  onToggleBrandingPreview,
  logoImage,
  currentDesignName,
  onApply,
  onCancel,
  maxLogoWidth,
  maxLogoHeight,
  horizontalPadding,
  headerHeight,
}) {
  const fileInputRef = useRef(null);
  const symbolIsAuto = pendingSymbolColor === "auto";
  const safeSymbolColor =
    pendingSymbolColor && pendingSymbolColor.match(/^#[0-9a-f]{6}$/i)
      ? pendingSymbolColor
      : "#000000";

  return (
    <div className="modal-backdrop">
      <div className="modal settings-modal modal-wide">
        <div className="modal-header">
          <h2>Branding &amp; Export Key Settings</h2>
          <button className="icon-btn" onClick={onCancel}>
            Close
          </button>
        </div>
        <div className="modal-body">
          <div className="setting-group">
            <h3>Design Information</h3>
            <label>
              Company/Designer Name for Export Header:
              <input
                type="text"
                value={pendingCompanyName}
                onChange={(event) => setPendingCompanyName(event.target.value)}
                placeholder="e.g., Cross-Stitch Company"
                className="input"
              />
            </label>
          </div>

          <hr />

          <div
            className="setting-group"
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}
          >
            <div>
              <h3>Logo Image</h3>
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={onLogoImport}
              />
              <p>Current Logo: {logoUrl ? "Loaded" : "None"}</p>
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt="Logo Preview"
                  style={{
                    maxWidth: "100px",
                    maxHeight: "100px",
                    border: "1px solid var(--border)",
                    padding: "5px",
                    marginBottom: "10px",
                  }}
                />
              )}
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  className="btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {logoUrl ? "Change Logo" : "Import Logo"}
                </button>
                {logoUrl && (
                  <button className="btn danger" onClick={onRemoveLogo}>
                    Remove
                  </button>
                )}
              </div>
              <p className="hint">
                Max size: {maxLogoWidth}x{maxLogoHeight} pixels.
              </p>
            </div>

            <div>
              <h3>Logo Positioning &amp; Size</h3>
              <div className="grid3">
                <label>
                  Alignment:
                  <select
                    value={pendingLogoAlignment}
                    onChange={(event) =>
                      setPendingLogoAlignment(event.target.value)
                    }
                    className="select"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </label>
                <label>
                  Margin (px):
                  <input
                    type="number"
                    value={pendingLogoMargin}
                    onChange={(event) => setPendingLogoMargin(event.target.value)}
                    min="0"
                    max="100"
                    className="input"
                  />
                </label>
                <label>
                  Size (%):
                  <input
                    type="number"
                    value={pendingLogoScale}
                    onChange={(event) => setPendingLogoScale(event.target.value)}
                    min="10"
                    max="200"
                    step="5"
                    className="input"
                  />
                </label>
              </div>
              <p className="hint">Size is % of the header height.</p>
            </div>
          </div>

          <hr />

          <div className="setting-group">
            <h3>Text Positioning &amp; Size</h3>
            <div className="grid3">
              <label>
                Alignment:
                <select
                  value={pendingTextAlignment}
                  onChange={(event) => setPendingTextAlignment(event.target.value)}
                  className="select"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </label>
              <label>
                Margin (px):
                <input
                  type="number"
                  value={pendingTextMargin}
                  onChange={(event) => setPendingTextMargin(event.target.value)}
                  min="0"
                  max="100"
                  className="input"
                />
              </label>
              <label>
                Size (%):
                <input
                  type="number"
                  value={pendingTextScale}
                  onChange={(event) => setPendingTextScale(event.target.value)}
                  min="50"
                  max="200"
                  step="10"
                  className="input"
                />
              </label>
            </div>
            <p className="hint">
              Size scales the default font size (100% is default).
            </p>
          </div>

          <hr />

          <div className="setting-group">
            <h3 style={{ display: "flex", justifyContent: "space-between" }}>
              Header Preview
              <button
                className={`btn ${showBrandingPreview ? "btn-good" : ""}`}
                onClick={onToggleBrandingPreview}
              >
                {showBrandingPreview ? "Hide Preview" : "Show Preview"}
              </button>
            </h3>
            {showBrandingPreview && (
              <BrandingPreviewCanvas
                logoImg={logoImage}
                companyName={pendingCompanyName}
                designName={currentDesignName}
                logoAlignment={pendingLogoAlignment}
                textAlignment={pendingTextAlignment}
                logoMargin={pendingLogoMargin}
                textMargin={pendingTextMargin}
                logoScale={pendingLogoScale}
                textScale={pendingTextScale}
                horizontalPadding={horizontalPadding}
                headerHeight={headerHeight}
              />
            )}
            <p className="hint">
              This shows a scaled preview of the header area only. The red line
              marks the center.
            </p>
          </div>

          <hr />

          <div className="setting-group">
            <h3>Symbol Contrast Override</h3>
            <div className="grid2">
              <label>
                Symbol Color:
                <select
                  value={symbolIsAuto ? "auto" : "custom"}
                  onChange={(event) =>
                    setPendingSymbolColor(
                      event.target.value === "auto" ? "auto" : safeSymbolColor
                    )
                  }
                  className="select"
                >
                  <option value="auto">Automatic (Contrast-Based)</option>
                  <option value="custom">Fixed Color</option>
                </select>
              </label>
              {!symbolIsAuto && (
                <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  Fixed Color:
                  <input
                    type="color"
                    value={safeSymbolColor}
                    onChange={(event) => setPendingSymbolColor(event.target.value)}
                    className="color-input-swatch"
                    title="Set a fixed color for all symbols"
                  />
                </label>
              )}
            </div>
            <p className="hint">
              Override the default black/white contrast for all symbols on the
              chart and key.
            </p>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={onApply}>
            Apply Branding
          </button>
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
